<template>
  <div class="editorjs-renderer">
    <template v-for="(block, index) in blocks">
      <!-- Paragraph -->
      <p v-if="block.type === 'paragraph'" :key="index" v-html="block.data.text"></p>

      <!-- Header -->
      <component
        v-else-if="block.type === 'header'"
        :is="'h' + block.data.level"
        :key="index"
        v-html="block.data.text"
      ></component>

      <!-- List (ordered/unordered) -->
      <component
        v-else-if="block.type === 'list'"
        :is="block.data.style === 'ordered' ? 'ol' : 'ul'"
        :key="index"
        class="editor-list"
      >
        <li v-for="(item, i) in block.data.items" :key="i" v-html="item"></li>
      </component>

      <!-- Checklist -->
      <ul v-else-if="block.type === 'checklist'" :key="index" class="editor-checklist">
        <li v-for="(item, i) in block.data.items" :key="i" :class="{ checked: item.checked }">
          <span class="checkbox">{{ item.checked ? '☑' : '☐' }}</span>
          <span v-html="item.text"></span>
        </li>
      </ul>

      <!-- Quote -->
      <blockquote v-else-if="block.type === 'quote'" :key="index" class="editor-quote">
        <p v-html="block.data.text"></p>
        <cite v-if="block.data.caption">{{ block.data.caption }}</cite>
      </blockquote>

      <!-- Code -->
      <pre v-else-if="block.type === 'code'" :key="index" class="editor-code"><code>{{ block.data.code }}</code></pre>

      <!-- Delimiter -->
      <hr v-else-if="block.type === 'delimiter'" :key="index" class="editor-delimiter" />

      <!-- Table -->
      <div v-else-if="block.type === 'table'" :key="index" class="table-responsive">
        <table class="table editor-table">
          <thead v-if="block.data.withHeadings && block.data.content.length > 0">
            <tr>
              <th v-for="(cell, ci) in block.data.content[0]" :key="ci" v-html="cell"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, ri) in block.data.withHeadings ? block.data.content.slice(1) : block.data.content"
              :key="ri"
            >
              <td v-for="(cell, ci) in row" :key="ci" v-html="cell"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Alert -->
      <div
        v-else-if="block.type === 'alert'"
        :key="index"
        class="editor-alert"
        :class="'alert-' + (block.data.type || 'info')"
      >
        <span v-html="block.data.message"></span>
      </div>

      <!-- Image -->
      <figure v-else-if="block.type === 'image'" :key="index" class="editor-image">
        <img :src="block.data.file?.url || block.data.url" :alt="block.data.caption || ''" />
        <figcaption v-if="block.data.caption">{{ block.data.caption }}</figcaption>
      </figure>

      <!-- Embed -->
      <div v-else-if="block.type === 'embed'" :key="index" class="editor-embed">
        <iframe
          :src="block.data.embed"
          :width="block.data.width || '100%'"
          :height="block.data.height || 320"
          frameborder="0"
          allowfullscreen
        ></iframe>
        <p v-if="block.data.caption" class="embed-caption">{{ block.data.caption }}</p>
      </div>

      <!-- Raw HTML -->
      <div v-else-if="block.type === 'raw'" :key="index" v-html="block.data.html"></div>

      <!-- Unknown block type - render as paragraph -->
      <p v-else :key="index" class="unknown-block">
        {{ block.data.text || JSON.stringify(block.data) }}
      </p>
    </template>
  </div>
</template>

<script>
export default {
  name: 'EditorJsRenderer',
  props: {
    content: {
      type: Object,
      required: true
    }
  },
  computed: {
    blocks() {
      if (!this.content) return [];
      // Handle both EditorJS format and direct blocks array
      return this.content.blocks || [];
    }
  }
};
</script>

<style scoped>
.editorjs-renderer {
  line-height: 1.6;
}

.editorjs-renderer p {
  margin-bottom: 1rem;
}

.editorjs-renderer h1,
.editorjs-renderer h2,
.editorjs-renderer h3,
.editorjs-renderer h4,
.editorjs-renderer h5,
.editorjs-renderer h6 {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.editor-list {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.editor-checklist {
  list-style: none;
  padding-left: 0;
  margin-bottom: 1rem;
}

.editor-checklist li {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.editor-checklist .checkbox {
  margin-right: 0.5rem;
  font-size: 1.1em;
}

.editor-checklist li.checked {
  text-decoration: line-through;
  opacity: 0.7;
}

.editor-quote {
  border-left: 3px solid #e9ecef;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
}

.editor-quote cite {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6c757d;
}

.editor-code {
  background: #171826;
  color: #e9ecef;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 1rem;
}

.editor-code code {
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 0.875rem;
}

.editor-delimiter {
  border: none;
  border-top: 2px solid #e9ecef;
  margin: 2rem 0;
}

.editor-table {
  margin-bottom: 1rem;
}

.editor-alert {
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.editor-alert.alert-info {
  background: rgba(23, 162, 184, 0.15);
  border-left: 4px solid #17a2b8;
}

.editor-alert.alert-warning {
  background: rgba(255, 193, 7, 0.15);
  border-left: 4px solid #ffc107;
}

.editor-alert.alert-danger {
  background: rgba(220, 53, 69, 0.15);
  border-left: 4px solid #dc3545;
}

.editor-alert.alert-success {
  background: rgba(40, 167, 69, 0.15);
  border-left: 4px solid #28a745;
}

.editor-image {
  margin: 1rem 0;
  text-align: center;
}

.editor-image img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.editor-image figcaption {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6c757d;
}

.editor-embed {
  margin: 1rem 0;
}

.editor-embed iframe {
  max-width: 100%;
  border-radius: 4px;
}

.embed-caption {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6c757d;
  text-align: center;
}

.unknown-block {
  background: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #6c757d;
}
</style>
